import {
  deleteSubtitleSidecar,
  getSubtitleSidecar,
  saveSubtitleSidecar,
} from '@/infrastructure/storage'
import { createLogger } from '@/shared/logging/logger'
import type { MediaSubtitleSidecar } from '@/types/storage'
import type { AudioItem, TextItem, TimelineItem, TimelineTrack, VideoItem } from '@/types/timeline'
import { usePlaybackStore } from '@/shared/state/playback'
import { useSelectionStore } from '@/shared/state/selection'
import { useProjectStore } from '@/features/media-library/deps/projects'
import { useTimelineStore } from '@/features/media-library/deps/timeline-stores'
import {
  detectSubtitleSidecarFormat,
  parseSubtitleSidecarText,
} from '../utils/subtitle-sidecar-parser'
import {
  buildCaptionTextItems,
  buildCaptionTrackAbove,
  findCompatibleCaptionTrackForRanges,
  findGeneratedCaptionItemsForClip,
  getCaptionRangeForClip,
  getCaptionTextItemTemplate,
  isCaptionTrackCandidate,
} from '../utils/caption-items'

const logger = createLogger('SubtitleSidecarService')

interface ImportSubtitleSidecarOptions {
  language?: string
}

interface InsertSubtitleSidecarAsCaptionsOptions {
  clipIds?: readonly string[]
  replaceExisting?: boolean
}

interface InsertSubtitleSidecarAsCaptionsResult {
  insertedItemCount: number
  removedItemCount: number
}

type CaptionableClip = AudioItem | VideoItem

class SubtitleSidecarService {
  getSubtitleSidecar = getSubtitleSidecar

  async deleteSubtitleSidecar(mediaId: string): Promise<void> {
    await deleteSubtitleSidecar(mediaId)
  }

  async importSubtitleSidecar(
    mediaId: string,
    file: File,
    options: ImportSubtitleSidecarOptions = {},
  ): Promise<MediaSubtitleSidecar> {
    const rawText = await file.text()
    const format = detectSubtitleSidecarFormat({
      fileName: file.name,
      mimeType: file.type,
      rawText,
    })

    if (!format) {
      throw new Error('Unsupported subtitle sidecar format. Only .srt and .vtt are supported.')
    }

    const segments = parseSubtitleSidecarText(rawText, format)
    if (segments.length === 0) {
      throw new Error('Subtitle sidecar did not contain any valid cues.')
    }

    const existing = await getSubtitleSidecar(mediaId)
    const now = Date.now()
    const record: MediaSubtitleSidecar = {
      id: mediaId,
      mediaId,
      fileName: file.name,
      mimeType: file.type || (format === 'vtt' ? 'text/vtt' : 'application/x-subrip'),
      format,
      language: options.language,
      rawText,
      segments,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    }

    logger.info('importSubtitleSidecar parsed subtitle cues', {
      mediaId,
      fileName: file.name,
      format,
      cueCount: segments.length,
    })

    return saveSubtitleSidecar(record)
  }

  async insertSubtitleSidecarAsCaptions(
    mediaId: string,
    options: InsertSubtitleSidecarAsCaptionsOptions = {},
  ): Promise<InsertSubtitleSidecarAsCaptionsResult> {
    const sidecar = await getSubtitleSidecar(mediaId)
    if (!sidecar) {
      throw new Error('No subtitle sidecar found for this media item')
    }

    const timeline = useTimelineStore.getState()
    const project = useProjectStore.getState().currentProject
    const targetClips = this.resolveCaptionTargetClips(mediaId, options.clipIds)
    if (targetClips.length === 0) {
      throw new Error('Select a clip for this media, or place one on the timeline first')
    }

    const canvasWidth = project?.metadata.width ?? 1920
    const canvasHeight = project?.metadata.height ?? 1080
    const newTracks: TimelineTrack[] = [...timeline.tracks]
    const generatedCaptionIdsToRemove = options.replaceExisting
      ? new Set(
          targetClips.flatMap((clip) =>
            findGeneratedCaptionItemsForClip(timeline.items, clip.id, 'subtitle-import').map(
              (item) => item.id,
            ),
          ),
        )
      : new Set<string>()
    const plannedItems = timeline.items.filter((item) => !generatedCaptionIdsToRemove.has(item.id))
    const insertedItems: TextItem[] = []

    for (const clip of targetClips) {
      const clipRange = getCaptionRangeForClip(clip, sidecar.segments, timeline.fps)
      if (!clipRange) {
        continue
      }

      const existingGeneratedCaptions = options.replaceExisting
        ? findGeneratedCaptionItemsForClip(timeline.items, clip.id, 'subtitle-import')
        : []
      const preferredTrackId = this.resolvePreferredCaptionTrackId(
        newTracks,
        plannedItems,
        existingGeneratedCaptions,
        clipRange,
      )

      let targetTrack = preferredTrackId
        ? (newTracks.find((track) => track.id === preferredTrackId) ?? null)
        : findCompatibleCaptionTrackForRanges(newTracks, plannedItems, [
            { startFrame: clipRange.startFrame, endFrame: clipRange.endFrame },
          ])

      if (!targetTrack) {
        const clipTrack = newTracks.find((track) => track.id === clip.trackId)
        targetTrack = clipTrack
          ? buildCaptionTrackAbove(newTracks, clipTrack.order)
          : buildCaptionTrackAbove(newTracks, 0)
        newTracks.push(targetTrack)
        newTracks.sort((a, b) => a.order - b.order)
      }

      const clipCaptionItems = buildCaptionTextItems({
        mediaId,
        trackId: targetTrack.id,
        segments: sidecar.segments,
        clip,
        timelineFps: timeline.fps,
        canvasWidth,
        canvasHeight,
        sourceType: 'subtitle-import',
        sourceMetadata: {
          fileName: sidecar.fileName,
          language: sidecar.language,
        },
        styleTemplate: existingGeneratedCaptions[0]
          ? getCaptionTextItemTemplate(existingGeneratedCaptions[0])
          : undefined,
      })

      if (clipCaptionItems.length === 0) {
        continue
      }

      insertedItems.push(...clipCaptionItems)
      plannedItems.push(...clipCaptionItems)
    }

    if (insertedItems.length === 0 && generatedCaptionIdsToRemove.size === 0) {
      throw new Error('Subtitle sidecar does not overlap the selected clip source range')
    }

    const tracksChanged =
      newTracks.length !== timeline.tracks.length ||
      newTracks.some((track, index) => track.id !== timeline.tracks[index]?.id)
    if (tracksChanged) {
      timeline.setTracks(newTracks)
    }

    if (generatedCaptionIdsToRemove.size > 0) {
      timeline.removeItems([...generatedCaptionIdsToRemove])
    }

    if (insertedItems.length > 0) {
      timeline.addItems(insertedItems)
      useSelectionStore.getState().selectItems(insertedItems.map((item) => item.id))
    }

    return {
      insertedItemCount: insertedItems.length,
      removedItemCount: generatedCaptionIdsToRemove.size,
    }
  }

  private resolveCaptionTargetClips(
    mediaId: string,
    clipIds?: readonly string[],
  ): CaptionableClip[] {
    const timeline = useTimelineStore.getState()
    const selection = useSelectionStore.getState()
    const playheadFrame = usePlaybackStore.getState().currentFrame

    const matchingClips = timeline.items
      .filter(
        (item): item is CaptionableClip =>
          (item.type === 'video' || item.type === 'audio') && item.mediaId === mediaId,
      )
      .sort((a, b) => a.from - b.from)

    if (matchingClips.length === 0) {
      return []
    }

    if (clipIds && clipIds.length > 0) {
      const requestedClipIds = new Set(clipIds)
      return matchingClips.filter((clip) => requestedClipIds.has(clip.id))
    }

    const selectedClips = selection.selectedItemIds
      .map((id) => matchingClips.find((clip) => clip.id === id))
      .filter((clip): clip is CaptionableClip => clip !== undefined)

    if (selectedClips.length > 0) {
      return selectedClips
    }

    if (matchingClips.length === 1) {
      return matchingClips
    }

    const clipAtPlayhead = matchingClips.find(
      (clip) => playheadFrame >= clip.from && playheadFrame < clip.from + clip.durationInFrames,
    )
    if (clipAtPlayhead) {
      return [clipAtPlayhead]
    }

    return []
  }

  private resolvePreferredCaptionTrackId(
    tracks: readonly TimelineTrack[],
    items: readonly TimelineItem[],
    existingCaptions: ReadonlyArray<{ trackId: string }>,
    range: { startFrame: number; endFrame: number },
  ): string | null {
    const trackIds = [...new Set(existingCaptions.map((item) => item.trackId))]
    if (trackIds.length !== 1) {
      return null
    }

    const preferredTrack = tracks.find((track) => track.id === trackIds[0])
    if (!preferredTrack || !isCaptionTrackCandidate(preferredTrack, items)) {
      return null
    }

    const hasOverlap = items.some((item) => {
      if (item.trackId !== preferredTrack.id) {
        return false
      }

      const itemEnd = item.from + item.durationInFrames
      return item.from < range.endFrame && itemEnd > range.startFrame
    })

    return hasOverlap ? null : preferredTrack.id
  }
}

export const subtitleSidecarService = new SubtitleSidecarService()